import { openReportWithAuth } from "../util";
import type { PaginatedResponse } from "./courseCertificates";
import { cleApi, getCleApiPrefix } from "./client";
import type {
  LegalAidEvidenceListModel,
  LegalAidEvidenceModel,
  LegalAidCategory,
} from "./models/legalAid";
import axios from "axios";

export type {
  LegalAidEvidenceListModel,
  LegalAidEvidenceModel,
  LegalAidCategory,
} from "./models/legalAid";

export interface GetLegalAidsParams {
  userId: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface GetLegalAidByIdParams {
  userId: string;
  legalAidId: string;
}

export interface GetLegalAidsByPeriodParams {
  userId: string;
  periodId: string;
}

export interface GetLegalAidsByInjunctionParams {
  userId: string;
  injunctionId: string;
}

export interface GetLegalAidsByImpositionParams {
  userId: string;
  impositionId: string;
}

export interface UpdateLegalAidRequest {
  category?: LegalAidCategory;
  number_of_lessons?: number;
}

export interface UpdateLegalAidParams {
  userId: string;
  legalAidId: string;
  body: UpdateLegalAidRequest;
}

export const getLegalAids = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: GetLegalAidsParams): Promise<
  PaginatedResponse<LegalAidEvidenceListModel>
> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  if (reported !== undefined) {
    query.set("reported", String(reported));
  }

  return cleApi.get<PaginatedResponse<LegalAidEvidenceListModel>>(
    `users/${encodeURIComponent(userId)}/legal-aid?${query.toString()}`,
  );
};

export const getLegalAidById = ({
  userId,
  legalAidId,
}: GetLegalAidByIdParams): Promise<LegalAidEvidenceModel> =>
  cleApi.get<LegalAidEvidenceModel>(
    `users/${encodeURIComponent(userId)}/legal-aid/${encodeURIComponent(legalAidId)}`,
  );

export const updateLegalAid = ({
  userId,
  legalAidId,
  body,
}: UpdateLegalAidParams): Promise<LegalAidEvidenceModel> =>
  cleApi.put<LegalAidEvidenceModel>(
    `users/${encodeURIComponent(userId)}/legal-aid/${encodeURIComponent(legalAidId)}`,
    body,
  );

export const getLegalAidsByPeriodId = ({
  userId,
  periodId,
}: GetLegalAidsByPeriodParams): Promise<LegalAidEvidenceListModel[]> =>
  cleApi.get<LegalAidEvidenceListModel[]>(
    `users/${encodeURIComponent(userId)}/legal-aid/by-period/${encodeURIComponent(periodId)}`,
  );

export const getLegalAidsByInjunctionId = ({
  userId,
  injunctionId,
}: GetLegalAidsByInjunctionParams): Promise<LegalAidEvidenceListModel[]> =>
  cleApi.get<LegalAidEvidenceListModel[]>(
    `users/${encodeURIComponent(userId)}/legal-aid/by-injunction/${encodeURIComponent(injunctionId)}`,
  );

export const getLegalAidsByImpositionId = ({
  userId,
  impositionId,
}: GetLegalAidsByImpositionParams): Promise<LegalAidEvidenceListModel[]> =>
  cleApi.get<LegalAidEvidenceListModel[]>(
    `users/${encodeURIComponent(userId)}/legal-aid/by-imposition/${encodeURIComponent(impositionId)}`,
  );

export const openLegalAidDownload = ({
  userId,
  legalAidId,
}: GetLegalAidByIdParams): Promise<void> =>
  openReportWithAuth(
    `${getCleApiPrefix()}users/${encodeURIComponent(userId)}/legal-aid/${encodeURIComponent(legalAidId)}/download`,
  );

export const getLegalAidPdfBlob = async ({
  userId,
  legalAidId,
}: GetLegalAidByIdParams): Promise<Blob> => {
  const response = await axios.get<Blob>(
    `${getCleApiPrefix()}users/${encodeURIComponent(userId)}/legal-aid/${encodeURIComponent(legalAidId)}/download`,
    { responseType: "blob" },
  );

  return response.data;
};
